import { useState, useEffect, useRef, useCallback } from 'react';
import { encodeJoinQueue, encodeLeaveQueue, decodeServerEnvelope } from '../services/protoService';
import { generateUUID } from '../utils/uuid';

const WS_BASE = import.meta.env.VITE_NETTY_WS_URL || `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.hostname}:8080`;
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 10_000];

function storageKey(shopId) { return `vq_session_${shopId}`; }

function loadSession(shopId) {
  try {
    const raw = localStorage.getItem(storageKey(shopId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveSession(shopId, session) {
  if (session) localStorage.setItem(storageKey(shopId), JSON.stringify(session));
  else localStorage.removeItem(storageKey(shopId));
}

// ─────────────────────────────────────────────────────────────────────────────

export function useQueue(shopId, { ephemeral = false } = {}) {
  const [phase, setPhase] = useState('idle');
  const [myEntry, setMyEntry] = useState(null);
  const [graceHold, setGraceHold] = useState(null);
  const [deskLabel, setDeskLabel] = useState('');
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [serverError, setServerError] = useState(null);
  const [session, _setSession] = useState(() => ephemeral ? null : loadSession(shopId));

  const phaseRef = useRef(phase);
  const sessionRef = useRef(session);
  const wsRef = useRef(null);
  const retryRef = useRef({ count: 0, timer: null });
  const activeRef = useRef(true);

  const updatePhase = (p) => { phaseRef.current = p; setPhase(p); };
  const updateSession = useCallback((s) => {
    sessionRef.current = s;
    _setSession(s);
    if (!ephemeral) saveSession(shopId, s);
  }, [shopId, ephemeral]);

  const closeWs = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    if (ws.readyState < WebSocket.CLOSING) ws.close();
    wsRef.current = null;
  }, []);

  const clearRetry = useCallback(() => {
    if (retryRef.current.timer) {
      clearTimeout(retryRef.current.timer);
      retryRef.current.timer = null;
    }
  }, []);

  const openWsRef = useRef(null);

  const scheduleRetry = useCallback(() => {
    if (!activeRef.current || !sessionRef.current) return;
    clearRetry();
    // Jitter ±40%: prevents thousands of clients from reconnecting in lock-
    // step after a backend restart and thundering the server.
    const base = RETRY_DELAYS_MS[Math.min(retryRef.current.count, RETRY_DELAYS_MS.length - 1)];
    const delay = base * (0.6 + Math.random() * 0.8);
    retryRef.current.count++;
    setRetryAttempt(retryRef.current.count);
    retryRef.current.timer = setTimeout(() => {
      if (activeRef.current && sessionRef.current) openWsRef.current?.();
    }, delay);
  }, [clearRetry]);

  /**
   * Force an immediate reconnect attempt, bypassing the exponential
   * backoff timer. Used by the online / visibilitychange listeners so
   * users don't wait up to 10 seconds after tab resume or network
   * recovery for the scheduled retry to fire.
   */
  const forceReconnectNow = useCallback(() => {
    if (!activeRef.current || !sessionRef.current) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) return; // already good
    clearRetry();
    retryRef.current.count = 0;
    setRetryAttempt(0);
    openWsRef.current?.();
  }, [clearRetry]);

  const handleEnvelope = useCallback((env) => {
    const sess = sessionRef.current;
    if (!sess) return;

    if (env.queueUpdate) {
      const entry = env.queueUpdate.entries?.find(e => e.ticketUuid === sess.ticketUuid);
      if (entry) {
        setMyEntry({ position: entry.position, estimatedWaitSeconds: entry.estimatedWaitSeconds, joinedAtMs: entry.joinedAtMs, ticketNumber: entry.ticketNumber });
        if (phaseRef.current === 'joining' || phaseRef.current === 'idle') {
          updatePhase('in-queue');
        }
      } else if (phaseRef.current === 'in-queue') {
        updatePhase('completed');
        updateSession(null);
        closeWs();
        clearRetry();
      }
    } else if (env.callNext) {
      if (env.callNext.ticketUuid === sess.ticketUuid) {
        setDeskLabel(env.callNext.deskLabel || '');
        updatePhase('called');
        setMyEntry(null);
      }
    } else if (env.skipNotify) {
      if (env.skipNotify.ticketUuid === sess.ticketUuid) {
        updatePhase('grace-hold');
        setGraceHold({
          graceHoldSeconds: env.skipNotify.graceHoldSeconds,
          expiresAtMs: env.skipNotify.expiresAtMs,
        });
      }
    } else if (env.completed) {
      if (env.completed.ticketUuid === sess.ticketUuid) {
        updatePhase('completed');
        updateSession(null);
        closeWs();
        clearRetry();
      }
    } else if (env.error) {
      setServerError(env.error.errorCode || 'generic');
    }
  }, [closeWs, clearRetry, updateSession]);

  const openWs = useCallback(() => {
    if (!activeRef.current || !sessionRef.current) return;
    closeWs();
    clearRetry();

    setWsStatus('connecting');
    const ws = new WebSocket(`${WS_BASE}/queue?shopId=${encodeURIComponent(shopId)}`);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = async () => {
      if (!activeRef.current) { ws.close(); return; }
      retryRef.current.count = 0;
      setRetryAttempt(0);
      setWsStatus('connected');

      const sess = sessionRef.current;
      if (!sess) return;
      try {
        const bytes = await encodeJoinQueue({
          requestId: generateUUID(),
          ticketUuid: sess.ticketUuid,
          shopId,
          firstName: sess.firstName || '',
          avatarId: sess.avatarId || '',
          serviceId: sess.serviceId || '',
          turnstileToken: sess.turnstileToken || '',
          qrToken: sess.qrToken || '',
        });
        if (ws.readyState === WebSocket.OPEN) ws.send(bytes);
      } catch (e) {
        console.error('Failed to encode JoinQueueRequest', e);
        setServerError('ENCODING_ERROR');
      }
    };

    ws.onmessage = async ({ data }) => {
      if (!activeRef.current) return;
      try {
        const env = await decodeServerEnvelope(data);
        handleEnvelope(env);
      } catch (e) {
        console.error('Failed to decode ServerEnvelope', e);
      }
    };

    ws.onclose = () => {
      if (!activeRef.current) return;
      setWsStatus('disconnected');
      const p = phaseRef.current;
      if (p !== 'idle' && p !== 'completed') scheduleRetry();
    };

    ws.onerror = () => { /* onclose fires next */ };
  }, [shopId, closeWs, clearRetry, handleEnvelope, scheduleRetry]);

  openWsRef.current = openWs;

  useEffect(() => {
    activeRef.current = true;
    if (sessionRef.current) {
      updatePhase('joining');
      openWs();
    }

    // Snap the WebSocket back immediately when connectivity or tab
    // focus returns, instead of waiting for the scheduled backoff
    // timer (which can be up to ~10s after many failed attempts).
    const onOnline = () => forceReconnectNow();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') forceReconnectNow();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      activeRef.current = false;
      clearRetry();
      closeWs();
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Public API ──────────────────────────────────────────────────────────────

  const join = useCallback(({ avatarId, avatarName, serviceId, turnstileToken, qrToken }) => {
    const ticketUuid = generateUUID();
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    const sess = { ticketUuid, pin, firstName: avatarName, avatarId, serviceId: serviceId || '', turnstileToken: turnstileToken || '', qrToken: qrToken || '' };
    updateSession(sess);
    updatePhase('joining');
    openWs();
  }, [updateSession, openWs]);

  const leave = useCallback(async () => {
    const sess = sessionRef.current;
    if (sess && wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        const bytes = await encodeLeaveQueue({
          requestId: generateUUID(),
          ticketUuid: sess.ticketUuid,
          shopId,
          serviceId: sess.serviceId || '',
        });
        wsRef.current.send(bytes);
      } catch (e) {
        console.error('Failed to encode LeaveQueueRequest', e);
      }
    }
    clearRetry();
    closeWs();
    updateSession(null);
    updatePhase('idle');
    setMyEntry(null);
    setGraceHold(null);
    setWsStatus('disconnected');
    setServerError(null);
  }, [shopId, clearRetry, closeWs, updateSession]);

  const recoverWithPin = useCallback((enteredPin) => {
    const sess = sessionRef.current;
    if (!sess) return 'no-session';
    if (enteredPin.trim() !== sess.pin) return 'wrong-pin';
    updatePhase('joining');
    openWs();
    return 'ok';
  }, [openWs]);

  return {
    phase,
    myEntry,
    graceHold,
    deskLabel,
    wsStatus,
    retryAttempt,
    serverError,
    session,
    join,
    leave,
    recoverWithPin,
  };
}
